/**
 * Customer Detail Page
 *
 * Refactored to use modular sub-components for improved maintainability.
 * All state management is handled by the useCustomerDetail hook.
 */

import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Loader2,
  ArrowLeft,
  Star,
  CheckCircle2,
  Package,
  Mail,
  Power,
  Camera,
  ShieldAlert,
  ShieldCheck,
  Shield,
  ShieldX,
} from 'lucide-react';
import { getRiskLevelStyle } from './customers-list/utils';
import { customersAPI } from '../lib/api';
import { useFormatters } from '../contexts/PreferencesContext';
import { useMenuSettings } from '../contexts/MenuSettingsContext';
import { useView } from '../contexts/ViewContext';
import { toast } from 'sonner';

// Import modular components
import {
  useCustomerDetail,
  OverviewTab,
  PerformanceTab,
  OrdersTab,
  ContractsTab,
  QualityTab,
  UsersTab,
  UserModal,
  ActivityTab,
  CreditScoreTab,
  SettingsTab,
  DocumentsTab,
} from './customer-detail';
import GroupStructureTab from './customer-detail/GroupStructureTab';

export default function CustomerDetail() {
  const navigate = useNavigate();
  const { formatCurrency: formatCurrencyFromHook } = useFormatters();
  const { customerProfileLayout } = useMenuSettings();
  const { getViewQueryParams } = useView();

  const {
    // State
    customer,
    orders,
    ncrs,
    users,
    loading,
    ordersLoading,
    ncrsLoading,
    usersLoading,
    error,
    activeTab,
    selectedUser,
    userModalOpen,
    userModalTab,
    userSitePermissions,
    permissionsLoading,
    savingPermission,
    impersonating,
    otifSettings,
    otifLoading,
    otifSaving,

    // Actions
    setActiveTab,
    handleOpenUserModal,
    handleCloseUserModal,
    handleToggleSiteLink,
    handleUpdatePermission,
    setUserSearch,
    handleOtifSettingsUpdate,
    setOtifSettings,
    handleUpdateProfile,
    handleUpdateCompany,
    handleUploadLogo,
    handleImpersonate,
    setUserModalTab,
    fetchCustomerData,
    fetchUsers,

    // Computed
    filteredUsers,
    isHelixAdmin,
    isSupplier,
    isOwnCompany,
    userSearch,
  } = useCustomerDetail();

  const [toggling, setToggling] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const canEditLogo = isHelixAdmin || isSupplier || isOwnCompany;

  const onLogoFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo must be 5MB or smaller');
      return;
    }
    try {
      setUploadingLogo(true);
      await handleUploadLogo(file);
      toast.success('Logo updated');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to upload logo';
      toast.error(message);
    } finally {
      setUploadingLogo(false);
    }
  };

  // Format currency helper
  const formatCurrency = (value: string | number) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return formatCurrencyFromHook(num);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error: {error || 'Supplier not found'}</p>
          <Button onClick={() => navigate('/customers')} variant="outline" className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Customers
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate('/customers')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          {(() => {
            const logo = (customer.company_details as unknown as Record<string, unknown>)?.['logo_url'] as string | undefined;
            return (
              <div className="relative w-24 h-24 rounded-lg border bg-white flex items-center justify-center overflow-hidden shrink-0 p-2 group">
                {logo ? (
                  <img src={logo} alt={customer.company_name} className="max-w-full max-h-full object-contain" />
                ) : (
                  <span className="text-3xl font-semibold text-muted-foreground">
                    {customer.company_name.trim().charAt(0).toUpperCase() || '?'}
                  </span>
                )}
                {canEditLogo && (
                  <>
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={onLogoFileChosen}
                    />
                    <button
                      type="button"
                      aria-label="Change company logo"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={uploadingLogo}
                      className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/50 text-white opacity-0 group-hover:opacity-100 transition disabled:opacity-60"
                    >
                      {uploadingLogo ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <>
                          <Camera className="h-5 w-5 mr-1" />
                          <span className="text-xs font-medium">Change</span>
                        </>
                      )}
                    </button>
                  </>
                )}
              </div>
            );
          })()}
          <div>
            <div className="flex items-center gap-3 mb-2">
              {customer.is_preferred && (
                <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
              )}
              <h1 className="text-3xl font-bold tracking-tight">{customer.company_name}</h1>
              <Badge
                className={
                  (customer as unknown as Record<string, unknown>)['onboarding_status'] === 'approved' || customer.is_approved
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : (customer as unknown as Record<string, unknown>)['onboarding_status'] === 'not_approved'
                    ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    : (customer as unknown as Record<string, unknown>)['onboarding_status'] === 'pending_initial'
                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                    : (customer as unknown as Record<string, unknown>)['onboarding_status'] === 'invited'
                    ? 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                    : (customer as unknown as Record<string, unknown>)['onboarding_status'] === 'due_diligence'
                    ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200'
                    : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                }
              >
                {(() => {
                  const status = (customer as unknown as Record<string, unknown>)['onboarding_status'] as string;
                  switch (status) {
                    case 'approved': return 'Approved';
                    case 'not_approved': return 'Not Approved';
                    case 'pending_initial': return 'Pending Initial Approval';
                    case 'invited': return 'Invited';
                    case 'due_diligence': return 'Due Diligence';
                    case 'draft': return 'Draft';
                    default: return customer.is_approved ? 'Approved' : 'Under Review';
                  }
                })()}
              </Badge>
              {/* CR-4771: Risk Level badge — same styling as the cards */}
              {customer.risk_level && customer.risk_level !== 'none' && (() => {
                const style = getRiskLevelStyle(customer.risk_level);
                const Icon = style.icon === 'critical' ? ShieldX
                  : style.icon === 'high' ? ShieldAlert
                  : style.icon === 'low' ? ShieldCheck
                  : Shield;
                return (
                  <Badge className={`text-xs font-medium px-2.5 py-1 ${style.badgeColor}`}>
                    <Icon className="h-3.5 w-3.5 mr-1" />
                    {style.label}
                  </Badge>
                );
              })()}
              {/* CR-4771: Credit Score badge — banding matches CustomerCard */}
              {typeof customer.latest_credit_score === 'number' && (() => {
                const score = customer.latest_credit_score;
                const band = score >= 800
                  ? { label: 'Excellent', cls: 'bg-green-50 text-green-700 border-green-200' }
                  : score >= 700
                    ? { label: 'Good', cls: 'bg-blue-50 text-blue-700 border-blue-200' }
                    : score >= 600
                      ? { label: 'Fair', cls: 'bg-amber-50 text-amber-700 border-amber-200' }
                      : { label: 'Poor', cls: 'bg-red-50 text-red-700 border-red-200' };
                return (
                  <Badge className={`text-xs font-medium px-2.5 py-1 ${band.cls}`} title={`Credit Score ${score} — ${band.label}`}>
                    Credit {score}
                  </Badge>
                );
              })()}
              {/* CR-4056: Active/Inactive Toggle */}
              <button
                onClick={async () => {
                  try {
                    setToggling(true);
                    const newState = !customer.company_details.is_active;
                    await customersAPI.toggleCustomerActive(customer.company, newState, getViewQueryParams());
                    toast.success(`Supplier ${newState ? 'activated' : 'deactivated'} successfully`);
                    await fetchCustomerData();
                  } catch (err: unknown) {
                    const error = err as { response?: { data?: { detail?: string } } };
                    toast.error(error.response?.data?.detail || 'Failed to update customer status');
                  } finally {
                    setToggling(false);
                  }
                }}
                disabled={toggling}
                className={`ml-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors disabled:opacity-50 ${
                  customer.company_details.is_active
                    ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
                    : 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'
                }`}
                title={`Click to ${customer.company_details.is_active ? 'deactivate' : 'activate'} customer`}
              >
                {toggling ? <Loader2 className="h-3 w-3 animate-spin" /> : <Power className="h-3 w-3" />}
                {customer.company_details.is_active ? 'Active' : 'Inactive'}
              </button>
            </div>
            <p className="text-muted-foreground">
              {customer.company_details.company_type === 'customer' ? 'Customer' : 'Company'} Profile
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => {
              const email = customer.company_details.primary_email;
              if (email) {
                window.open(`mailto:${email}`, '_blank');
              } else {
                toast.error('No contact email available for this customer');
              }
            }}
          >
            <Mail className="h-4 w-4 mr-2" />
            Contact
          </Button>
        </div>
      </div>

      {/* Key Metrics Cards */}
      {(customerProfileLayout.stats.otd || customerProfileLayout.stats.total_orders) && (
        <div className={`grid gap-4 ${customerProfileLayout.stats.otd && customerProfileLayout.stats.total_orders ? 'md:grid-cols-2' : 'md:grid-cols-1'}`}>
          {customerProfileLayout.stats.otd && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4" />
                  On-Time Delivery
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  {customer.on_time_delivery_rate.toFixed(1)}%
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {customer.ontime_count} of {customer.grn_count} GRNs
                </p>
              </CardContent>
            </Card>
          )}
          {customerProfileLayout.stats.total_orders && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  Total Orders
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{customer.total_orders}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {customer.completed_orders} completed
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Company Overview</TabsTrigger>
          {customerProfileLayout.tabs.performance && <TabsTrigger value="performance">Performance</TabsTrigger>}
          {customerProfileLayout.tabs.orders && <TabsTrigger value="orders">Orders</TabsTrigger>}
          {customerProfileLayout.tabs.contracts && <TabsTrigger value="contracts">Contracts</TabsTrigger>}
          {customerProfileLayout.tabs.quality && <TabsTrigger value="quality">Quality</TabsTrigger>}
          {customerProfileLayout.tabs.credit_scores && <TabsTrigger value="credit-scores">Credit Scores</TabsTrigger>}
          {customerProfileLayout.tabs.users && <TabsTrigger value="users">Users</TabsTrigger>}
          {/* Defaults to visible when an existing tenant's layout JSON
              predates the documents key. Bracket access because documents
              isn't an explicit field on CustomerProfileLayout.tabs. */}
          {(customerProfileLayout.tabs['documents'] ?? true) && <TabsTrigger value="documents">Documents</TabsTrigger>}
          {/* CR-4773: Renamed to match the card content ("Communication Audit Log") */}
          {customerProfileLayout.tabs.activity_log && <TabsTrigger value="activity">Communication Log</TabsTrigger>}
          <TabsTrigger value="settings">Settings</TabsTrigger>
          {customerProfileLayout.tabs.group_structure && <TabsTrigger value="group-structure">Group Structure</TabsTrigger>}
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <OverviewTab
            customer={customer}
            formatCurrency={formatCurrency}
            canEdit={isHelixAdmin || isSupplier}
            onUpdateProfile={handleUpdateProfile}
            onUpdateCompany={handleUpdateCompany}
          />
        </TabsContent>

        {/* Performance Tab */}
        <TabsContent value="performance" className="space-y-4">
          <PerformanceTab customer={customer} />
        </TabsContent>

        {/* Orders Tab */}
        <TabsContent value="orders" className="space-y-4">
          <OrdersTab
            customer={customer}
            orders={orders}
            ordersLoading={ordersLoading}
            formatCurrency={formatCurrency}
          />
        </TabsContent>

        {/* Contracts Tab */}
        <TabsContent value="contracts" className="space-y-4">
          <ContractsTab customer={customer} />
        </TabsContent>

        {/* Quality Tab */}
        <TabsContent value="quality" className="space-y-4">
          <QualityTab customer={customer} ncrs={ncrs} ncrsLoading={ncrsLoading} />
        </TabsContent>

        {/* Credit Scores Tab */}
        <TabsContent value="credit-scores" className="space-y-4">
          <CreditScoreTab customer={customer} />
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-4">
          <UsersTab
            users={users}
            filteredUsers={filteredUsers}
            usersLoading={usersLoading}
            userSearch={userSearch}
            impersonating={impersonating}
            isHelixAdmin={isHelixAdmin}
            isSupplier={isSupplier}
            companyId={customer?.company || 0}
            companyName={customer?.company_name || ''}
            groupId={customer?.company_details?.group}
            onUserSearchChange={setUserSearch}
            onOpenUserModal={handleOpenUserModal}
            onImpersonate={handleImpersonate}
            onRefreshUsers={fetchUsers}
          />
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="space-y-4">
          <DocumentsTab customer={customer} />
        </TabsContent>

        {/* CR-4477: Activity / Traceability Log Tab */}
        <TabsContent value="activity" className="space-y-4">
          <ActivityTab customer={customer} />
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-4">
          <SettingsTab
            customer={customer}
            otifSettings={otifSettings}
            otifLoading={otifLoading}
            otifSaving={otifSaving}
            isHelixAdmin={isHelixAdmin}
            isSupplier={isSupplier}
            onOtifSettingsUpdate={handleOtifSettingsUpdate}
            setOtifSettings={setOtifSettings}
            onUpdateProfile={handleUpdateProfile}
          />
        </TabsContent>

        {/* Group Structure Tab */}
        <TabsContent value="group-structure" className="space-y-4">
          <GroupStructureTab
            customer={customer}
            isHelixAdmin={isHelixAdmin}
            isSupplier={isSupplier}
          />
        </TabsContent>
      </Tabs>

      {/* User Detail Modal */}
      <UserModal
        open={userModalOpen}
        onOpenChange={handleCloseUserModal}
        selectedUser={selectedUser}
        userModalTab={userModalTab}
        onTabChange={setUserModalTab}
        userSitePermissions={userSitePermissions}
        permissionsLoading={permissionsLoading}
        savingPermission={savingPermission}
        impersonating={impersonating}
        isHelixAdmin={isHelixAdmin}
        isSupplier={isSupplier}
        contextCompanyName={customer?.company_name}
        onToggleSiteLink={handleToggleSiteLink}
        onUpdatePermission={handleUpdatePermission}
        onImpersonate={handleImpersonate}
      />
    </div>
  );
}
